import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { BookOpen } from 'lucide-react';
import { Button } from 'react-bootstrap';
import Modal from 'components/Modal/Modal';
import styles from './ResumeProgressModal.module.scss';

const TITLE_ID = 'PostAJobResumeProgress-title';

const ResumeProgressModal = ({ show, onStartAgain, onContinue }) => {
  const actionsRef = useRef(null);
  const previousFocusRef = useRef(null);

  useEffect(() => {
    if (!show || typeof document === 'undefined') {
      return undefined;
    }

    previousFocusRef.current = document.activeElement;

    const actions = actionsRef.current;
    const buttons = actions
      ? Array.from(actions.querySelectorAll('button'))
      : [];
    const continueButton = buttons[buttons.length - 1];

    if (continueButton && typeof continueButton.focus === 'function') {
      continueButton.focus();
    }

    const handleKeyDown = (event) => {
      if (event.key !== 'Tab' || buttons.length === 0) {
        return;
      }

      const first = buttons[0];
      const last = buttons[buttons.length - 1];
      const active = document.activeElement;

      if (event.shiftKey) {
        if (active === first || !buttons.includes(active)) {
          event.preventDefault();
          last.focus();
        }
        return;
      }

      if (active === last || !buttons.includes(active)) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);

      const previous = previousFocusRef.current;
      if (
        previous &&
        typeof previous.focus === 'function' &&
        document.contains(previous)
      ) {
        previous.focus();
      }
    };
  }, [show]);

  return (
    <Modal
      show={show}
      animation="scale"
      backgroundExit={false}
      modalStyle={{
        width: 'calc(100% - 40px)',
        maxWidth: 540,
        padding: '36px 40px 32px',
        borderRadius: 16,
        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.18)',
      }}
      className={styles.content}
    >
      <div className={styles.iconBadge} aria-hidden="true">
        <BookOpen size={28} strokeWidth={2} className={styles.icon} />
      </div>

      <h2 className={styles.title} id={TITLE_ID}>
        You have a submission in progress. Would you like to continue?
      </h2>

      <p className={styles.subtitle}>
        Starting again will erase all information you have entered.
      </p>

      <div className={styles.actions} ref={actionsRef}>
        <Button
          bsStyle="default"
          className={styles.startAgain}
          type="button"
          onClick={onStartAgain}
        >
          Start again
        </Button>
        <Button
          bsStyle="primary"
          className={styles.continue}
          type="button"
          onClick={onContinue}
        >
          Continue
        </Button>
      </div>
    </Modal>
  );
};

ResumeProgressModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onStartAgain: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
};

export default ResumeProgressModal;
